import { useMemo } from "react";

import router from "next/router";

import { useMutation } from "@tanstack/react-query";

import { AmplitudeApi } from "@/api/amplitude";
import { GetLeftInfoApi } from "@/api/left-panel";
import { useLeftBarData } from "@/api/left-panel/hooks";
import { GetLeftInfoResponseDTOSuccess } from "@/api/left-panel/models";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { EVENT_CODES } from "@/constants/eventCodes";
import { useMailList } from "@/features/mailbox/hooks";
import { MailListLetterResponseDto_Raw } from "@/features/mailbox/models/MailListLetter.dto";
import { useQueryCashed } from "@/hooks/useQueryCashed";

import { SendLetterApi } from "../SendLetter.api";
import { SendLetterResponseDTOError } from "../models/SendLetter.response.dto";

export const useSendLetter = (popup: boolean, ladyId?: number) => {
  const { openModal, removeAll } = useOpenModal();
  const { openModal: disableBGClickModal } = useOpenModal({
    disableBackGroundClick: true,
  });

  const { data } = useLeftBarData();

  const openPurchaseCoinsModal = useMemo(() => {
    if (data && "balance" in data && data.balance.isReplenished !== false) {
      return () => {
        disableBGClickModal("CREATE_PURCHASE_COINS_MODAL", {
          onClose: () => removeAll(),
        });
      };
    } else {
      return () => {
        disableBGClickModal("CREATE_ONE_TIME_PURCHASE_COINS_MODAL", {
          onClose: () => removeAll(),
        });
      };
    }
  }, [data, disableBGClickModal, removeAll]);
  const { refetch: letterRefetch } = useMailList({
    pageSize: 10,
    startIndex: 0,
    extraParams: { ladyId },
    responseSchema: MailListLetterResponseDto_Raw,
  });

  const openErrorModal = (message: string) => {
    openModal("INFORMATION_MODAL", {
      message,
      statusReg: "fail",
    });
  };

  const onOkClick = async () => {
    removeAll();
    const query = ladyId ? `?ladyId=${ladyId}` : "";

    await router.push(`/mailbox-inbox${query}`);
  };

  const openSuccessModal = (message: string) => {
    openModal("INFORMATION_MODAL", {
      message,
      statusReg: "success",
      onOkClick: onOkClick,
      disableonOkClickOnCross: true,
    });
  };

  const openPurchaseInfoModal = (message: string) => {
    disableBGClickModal("INFORMATION_MODAL", {
      message,
      statusReg: "success",
      buttonText: "Purchase coins",
      disableonOkClickOnCross: true,
      onOkClick: openPurchaseCoinsModal,
    });
  };
  const { refetch } = useQueryCashed({
    queryKey: ["LEFTBAR_DATA"],
    queryFn: async () => {
      const response = await GetLeftInfoApi.getLeftInfo();
      if (!GetLeftInfoResponseDTOSuccess.safeParse(response).success) {
        throw new Error("Invalid response format");
      }
      return response;
    },
  });

  return useMutation(SendLetterApi.sendLetter, {
    onSuccess: async (response) => {
      const errorDTO = SendLetterResponseDTOError.safeParse(response);
      if (errorDTO.success) {
        if (
          errorDTO.data.requestDTO.dataMap.eventCode ===
          EVENT_CODES.PAYMENT_MODAL
        ) {
          openPurchaseInfoModal(
            "Sorry, it looks like you're out of coins. Purchase more to keep in touch!",
          );
        } else {
          openErrorModal(errorDTO.data.requestDTO.dataMap.error);
        }
      } else {
        openSuccessModal("Your letter was sent.");

        AmplitudeApi.send({
          amplitude: "answer_mail",
          page: window.location.pathname,
          popup: popup,
        });
        refetch();

        letterRefetch();
      }
    },
    onError: async (error: TODO) => {
      console.log(error, "error response");

      const responseData = error?.response?.data;

      if (responseData?.requestDTO?.dataMap) {
        const dataMap = responseData.requestDTO.dataMap;

        if (dataMap.eventCode === EVENT_CODES.PAYMENT_MODAL) {
          openPurchaseInfoModal(
            dataMap.error ||
              "Sorry, it looks like you're out of coins. Purchase more to keep in touch!",
          );
        } else {
          openErrorModal(
            dataMap.error || "An error occurred while sending the letter",
          );
        }
      } else {
        openErrorModal("An error occurred while sending the letter");
      }
    },
  });
};
